import * as api from '@api';
import ErrorToast from '@components/ErrorToast';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@/hooks/useApi';
import useToast from '@/hooks/useToast';

/**
 * The impact-area dropdown list.
 *
 * Served by `GET /api/impact-areas` (`companies_service_impact_areas`) since
 * the 2026-09 remap. The ids matter: findings and actions now store the modern
 * `id`, so a dropdown still emitting the legacy random ids would write values
 * nothing can resolve.
 *
 * The rows keep the legacy field names — `hierarchyId`, `description` — because
 * ~30 CAPA views and the shared `ImpactAreasSelect` tree read them, and the
 * values are identical anyway: `sectionId` is the same dotted numbering and
 * `name` the same label. Renaming them across the old dashboard would be churn
 * with no behavioural gain.
 *
 * `childCount` is derived here rather than fetched. The legacy route computed
 * it in SQL as the number of *direct* children, and `impact-areas-select.jsx`
 * turns it into `isLeaf: !childCount` — without it every parent node would
 * collapse into a leaf and the tree would not expand.
 *
 * There is no admin variant: unlike the legacy route, `/api/impact-areas`
 * serves the same global taxonomy to every authenticated user.
 */
export default function useImpactAreasList() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);

  // Called through an arrow rather than passed by reference: several CAPA
  // test suites mock `@api` with only the functions they exercise, and a
  // bare `undefined` here makes the useQuery wrapper throw on render.
  const result = useQuery(
    'getImpactAreas',
    () => api.backend.listImpactAreas(),
    {
      gcTime: 24 * 3600 * 1000,
    },
  );

  useEffect(() => {
    if (result.isSuccess && result.data) {
      const { status, error, data: responseData } = result.data;
      if (status >= 400) {
        toast.custom(t => <ErrorToast t={t} error={error} />);
      } else {
        setRows(responseData?.data ?? []);
      }
    }
  }, [result.isSuccess, result.data]);

  return useMemo(() => {
    const sectionIds = rows.map(row => row.sectionId);

    return rows.map(row => {
      const childPrefix = `${row.sectionId}.`;
      const depth = row.sectionId.split('.').length;

      return {
        id: row.id,
        hierarchyId: row.sectionId,
        description: row.name,
        // Direct children only — a grandchild shares the prefix but sits a
        // level deeper, and counting it would mark a leaf's parent expandable.
        childCount: sectionIds.filter(
          sectionId =>
            sectionId.startsWith(childPrefix) &&
            sectionId.split('.').length === depth + 1,
        ).length,
      };
    });
  }, [rows]);
}
